import React, { useEffect, useRef } from 'react';
import ColorPicker from '../../common/ColorPicker';
import flatpickr from 'flatpickr';
import { Portuguese } from 'flatpickr/dist/l10n/pt';

interface TimerInspectorProps {
  config: {
    timerType?: 'minutes' | 'date';
    backgroundColor?: string;
    textColor?: string;
    timeValue?: number; // em minutos para timer tipo 'minutes'
    endDate?: string; // para timer tipo 'date'
    activeText?: string;
    expiredText?: string;
    fixedTop?: boolean;
  };
  onConfigChange: (newConfig: Partial<TimerInspectorProps['config']>) => void;
}

const TimerInspector: React.FC<TimerInspectorProps> = ({ config, onConfigChange }) => {
  const timerType = config.timerType || 'minutes';
  const backgroundColor = config.backgroundColor || '#ef4444';
  const textColor = config.textColor || '#ffffff';
  const timeValue = config.timeValue || 15;
  const endDate = config.endDate || '';
  const activeText = config.activeText || 'Oferta por tempo limitado';
  const expiredText = config.expiredText || 'O tempo acabou!';
  const fixedTop = config.fixedTop || false;
  
  const datePickerRef = useRef<HTMLInputElement>(null);

  const formatTimeForInput = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:00`;
  };

  const parseTimeFromInput = (timeStr: string): number => {
    const parts = timeStr.split(':');
    const hours = parseInt(parts[0] || '0');
    const minutes = parseInt(parts[1] || '0');
    return hours * 60 + minutes;
  };

  // Inicializar Flatpickr para seleção de data
  useEffect(() => {
    if (timerType === 'date' && datePickerRef.current) {
      const fp = flatpickr(datePickerRef.current, {
        enableTime: true,
        time_24hr: true,
        dateFormat: 'Y-m-d H:i',
        locale: Portuguese,
        defaultDate: endDate || new Date(Date.now() + 24 * 60 * 60 * 1000), // 24h a partir de agora
        minDate: new Date(),
        onChange: (selectedDates) => {
          if (selectedDates.length > 0) {
            const selectedDate = selectedDates[0];
            onConfigChange({ endDate: selectedDate.toISOString() });
          }
        }
      });

      return () => {
        fp.destroy();
      };
    }
  }, [timerType, endDate, onConfigChange]);

  return (
    <div className="space-y-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white">Cronômetro</h3>

      {/* Tipo */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Tipo
        </label>
        <select 
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={timerType}
          onChange={(e) => onConfigChange({ timerType: e.target.value as 'minutes' | 'date' })}
        >
          <option value="minutes">Tempo em minutos</option>
          <option value="date">Data final</option>
        </select>
      </div>

      {/* Background Color */}
      <div>
        <ColorPicker
          color={backgroundColor}
          onChange={(color) => onConfigChange({ backgroundColor: color })}
          label="Background"
        />
      </div>

      {/* Text Color */}
      <div>
        <ColorPicker
          color={textColor}
          onChange={(color) => onConfigChange({ textColor: color })}
          label="Texto"
        />
      </div>

      {/* Campo de Tempo - diferente para cada tipo */}
      {timerType === 'minutes' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Tempo
          </label>
          <input
            type="time"
            step="1"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={formatTimeForInput(timeValue)}
            onChange={(e) => {
              const minutes = parseTimeFromInput(e.target.value);
              onConfigChange({ timeValue: minutes });
            }}
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Data final
          </label>
          <input
            ref={datePickerRef}
            type="text"
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            placeholder="Selecionar data"
            readOnly
          />
        </div>
      )}

      {/* Texto contagem ativa */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Texto contagem ativa
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={activeText}
          onChange={(e) => onConfigChange({ activeText: e.target.value })}
          placeholder="Oferta por tempo limitado"
        />
      </div>

      {/* Texto contagem finalizada */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Texto contagem finalizada
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={expiredText}
          onChange={(e) => onConfigChange({ expiredText: e.target.value })}
          placeholder="O tempo acabou!"
        />
      </div>

      {/* Fixar no topo */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Fixar no topo
        </label>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={fixedTop}
            onChange={(e) => onConfigChange({ fixedTop: e.target.checked })}
            className="sr-only"
          />
          <div className={`w-11 h-6 rounded-full transition-colors ${
            fixedTop ? 'bg-blue-600' : 'bg-gray-200'
          }`}>
            <div className={`w-5 h-5 bg-white rounded-full shadow transform transition-transform ${
              fixedTop ? 'translate-x-5' : 'translate-x-0'
            } mt-0.5 ml-0.5`} />
          </div>
        </label>
      </div>
    </div>
  );
};

export default TimerInspector;
